import React, { ForwardRefRenderFunction, useEffect, useRef } from "react";
import mapboxgl, { LngLatBoundsLike } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import useMap from "./use-map";
import useMapEffects from "./use-map-effects";
import useMapStyleDiffApply from "./use-map-style-diff-apply";
import useMapStyle from "./use-map-style";

function mergeRefs<T = any>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

const Map: ForwardRefRenderFunction<HTMLDivElement, MapProps> = (
  {
    children,
    className,
    accessToken,
    style,
    bounds,
    fitBoundsOptions,
    cooperativeGestures,
    id,
  },
  ref
) => {
  const { setMap } = useMapEffects();
  const { map } = useMap();
  const initialised = useRef(false);
  const mapContainer = useRef<HTMLDivElement>(null);

  // Obtain the map and apply style changes
  const { mapStyle } = useMapStyle();
  useMapStyleDiffApply(mapStyle);

  useEffect(() => {
    if (mapContainer.current != null && map != null) {
      const ro = new ResizeObserver(() => {
        map.resize();
      });
      ro.observe(mapContainer.current);
    }
  }, [map]);

  useEffect(() => {
    if (
      initialised.current === false &&
      mapContainer.current != null &&
      mapStyle != null
    ) {
      // Access Token
      mapboxgl.accessToken = accessToken;
      // Create the map instance
      const mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        bounds: bounds || undefined,
        fitBoundsOptions,
        cooperativeGestures,
      });

      // Set into context
      setMap(mapInstance);

      initialised.current = true;
    }
  }, [
    map,
    setMap,
    bounds,
    accessToken,
    fitBoundsOptions,
    mapStyle,
    cooperativeGestures,
  ]);

  return (
    <div
      id={id}
      ref={mergeRefs([mapContainer, ref])}
      className={className}
      style={style}
    >
      {children}
    </div>
  );
};

export type MapProps = {
  id?: string;
  accessToken: string;
  className?: string;
  style?: any;
  // mapStyle: mapboxgl.Style | string | undefined;
  children?: React.ReactNode;
  bounds: LngLatBoundsLike;
  fitBoundsOptions?: mapboxgl.FitBoundsOptions;
  cooperativeGestures?: boolean;
};

export default React.memo(React.forwardRef(Map));
